import {getNewTokenValueFromAWS, getTokenValueFromAWS} from "../functions/AmplifyFunctions";
import {store} from "../app/store";
import {selectIdToken, setLoginState} from "../features/states/slices";

function getBaseUrl() {
    if (process.env.REACT_APP_STATE === "development") {
        return process.env.REACT_APP_DEV_ENDPOINT_BASE_URL;
    }
    if (process.env.REACT_APP_STATE === "production") {
        return process.env.REACT_APP_ENDPOINT_BASE_URL;
    }
    if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
        return process.env.REACT_APP_DEV_ENDPOINT_BASE_URL;
    }
    return process.env.REACT_APP_ENDPOINT_BASE_URL;
}

function assertRoute(route) {
    if (route === undefined || route === null || route.length === 0) {
        throw new Error("Invalid route exception");
    }
}

function buildUrl(route) {
    return `${getBaseUrl()}${route}`;
}

function buildUnauthorizedError() {
    const error = new Error("UNAUTHORIZED");
    error.status = 401;
    error.statusText = "Unauthorized";
    return error;
}

async function parseResponse(response) {
    if (response.status === 401) {
        store.dispatch(setLoginState(false));
        throw buildUnauthorizedError();
    }
    let payload = null;
    try {
        payload = await response.json();
    } catch (e) {
        payload = null;
    }

    if (!response.ok) {
        const error = new Error(response.statusText || "Request failed");
        error.status = response.status;
        error.statusText = response.statusText || "Request failed";
        error.data = payload;
        error.payload = payload;
        throw error;
    }

    return payload;
}

async function authHeaders(extraHeaders = {}) {
    let token = null;
    try {
        token = await getTokenValueFromAWS();
    } catch (error) {
        token = null;
    }

    if (!token) {
        store.dispatch(setLoginState(false));
        throw buildUnauthorizedError();
    }
    return {
        "Content-Type": "application/json",
        "accept": "application/json",
        "Authorization": `Bearer ${token}`,
        ...extraHeaders,
    };
}

export async function apiRequest({
    route,
    method = "GET",
    body,
    headers = {},
    requiresAuth = true,
}) {
    assertRoute(route);
    const resolvedHeaders = requiresAuth ? await authHeaders(headers) : headers;

    const options = {
        method,
        headers: resolvedHeaders,
    };

    if (body !== undefined && body !== null) {
        options.body = JSON.stringify(body);
    }

    let response = await fetch(buildUrl(route), options);

    if (requiresAuth && response.status === 401) {
        let refreshedToken = null;
        try {
            refreshedToken = await getNewTokenValueFromAWS();
        } catch (error) {
            refreshedToken = null;
        }

        const fallbackTokens = [];
        if (refreshedToken) {
            fallbackTokens.push(refreshedToken);
        }
        const refreshedIdToken = selectIdToken(store.getState());
        if (
            refreshedIdToken &&
            typeof refreshedIdToken === "string" &&
            refreshedIdToken !== refreshedToken
        ) {
            fallbackTokens.push(refreshedIdToken);
        }

        for (const token of fallbackTokens) {
            const retryOptions = {
                ...options,
                headers: {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                },
            };
            response = await fetch(buildUrl(route), retryOptions);
            if (response.status !== 401) {
                break;
            }
        }
    }

    return parseResponse(response);
}

export async function apiGet(route) {
    return apiRequest({route, method: "GET"});
}

export async function apiPost(route, body) {
    return apiRequest({route, method: "POST", body});
}

export async function apiPut(route, body) {
    return apiRequest({route, method: "PUT", body});
}

export async function apiDelete(route, body) {
    return apiRequest({route, method: "DELETE", body});
}

export async function apiRequestWithOptions(route, requestOptions = {}) {
    assertRoute(route);
    const response = await fetch(buildUrl(route), requestOptions);
    return parseResponse(response);
}
